import * as THREE from 'three'
import { FenceConfig, LoadedTextures, TextureConfig } from './types'
import { getFenceConfigWithColor } from '../../config/fenceConfigs'

// 🚀 DEBUG FLAG - ustaw na true tylko do debugowania
const DEBUG_LOGS = true

const MAX_TEXTURE_ANISOTROPY = 16
const LINEAR_COLOR_SPACE =
  ((THREE as unknown as { LinearSRGBColorSpace?: THREE.ColorSpace }).LinearSRGBColorSpace) ?? THREE.SRGBColorSpace

/**
 * 🚀 POZIOM 1: Globalny cache obiektów tekstur
 * Klucz: URL pliku → obiekt THREE.Texture
 * Zapewnia że ten sam plik jest ładowany tylko raz
 */
const globalTextureCache = new Map<string, THREE.Texture>()

/**
 * Counter dla unikalnego losowego offsetu (zamiast Date.now() który jest taki sam w szybkich wywołaniach)
 */
let textureOffsetCounter = 0

/**
 * 🚀 POZIOM 2: Promise cache dla równoległych wywołań
 * Klucz: URL pliku → Promise<THREE.Texture>
 * Zapewnia że równoległe wywołania czekają na ten sam Promise
 */
const textureLoadingPromises = new Map<string, Promise<THREE.Texture | undefined>>()

/**
 * Cache dla sprawdzania czy WebP istnieje (żeby nie robić wielokrotnych prób)
 * Klucz: jpg URL → boolean (true = webp istnieje, false = nie istnieje, undefined = nie sprawdzono)
 */
const webpExistsCache = new Map<string, boolean>()

/**
 * 🚀 POZIOM 3: Shared materials cache
 * Klucz: groupName + textureType + color → THREE.MeshStandardMaterial
 * Zapewnia że identyczne grupy używają tego samego materiału
 */
const sharedMaterialsCache = new Map<string, THREE.MeshStandardMaterial>()

/**
 * Ładuje teksturę z trójpoziomowym cache
 * 
 * KLUCZOWE: Cache używa ORYGINALNEGO URL (przed fetch), nie blob URL!
 */
async function loadTextureWithSettings(
  loader: THREE.TextureLoader,
  url: string,
  options: {
    colorMap?: boolean
    repeat?: [number, number]
  } = {}
): Promise<THREE.Texture | undefined> {
  // 🚀 NORMALIZUJ URL - usuń query params i hash dla spójnego cache key
  const normalizedUrl = url.split('?')[0].split('#')[0]
  
  try {
    // 🚀 POZIOM 1: Sprawdź globalny cache obiektów tekstur PRZED fetch
    const cachedTexture = globalTextureCache.get(normalizedUrl)
    if (cachedTexture) {
      DEBUG_LOGS && console.log(`✅ CACHE HIT (poziom 1): Tekstura już załadowana: ${normalizedUrl}`)
      
      // ❌ NIE używaj zwykłego .clone() - kopiuje źle właściwości!
      // ✅ Ręcznie skopiuj WSZYSTKIE właściwości z oryginału
      const cloned = cachedTexture.clone()
      
      // RĘCZNIE skopiuj WSZYSTKIE właściwości jakości z oryginału
      cloned.wrapS = cachedTexture.wrapS
      cloned.wrapT = cachedTexture.wrapT
      cloned.generateMipmaps = cachedTexture.generateMipmaps
      cloned.minFilter = cachedTexture.minFilter
      cloned.magFilter = cachedTexture.magFilter
      cloned.anisotropy = cachedTexture.anisotropy
      cloned.colorSpace = cachedTexture.colorSpace
      
      // Zastosuj opcje (repeat może być różny dla różnych użyć)
      if (options.repeat) {
        cloned.repeat.set(options.repeat[0], options.repeat[1])
      }
      
      // WYMUSZAJ UPDATE - to zapewnia że wszystkie ustawienia są zastosowane
      cloned.needsUpdate = true
      
      return cloned
    }

    // 🚀 POZIOM 2: Sprawdź Promise cache (dla równoległych wywołań) PRZED fetch
    const existingPromise = textureLoadingPromises.get(normalizedUrl)
    if (existingPromise) {
      DEBUG_LOGS && console.log(`⏳ CACHE HIT (poziom 2): Czekam na istniejące Promise dla: ${normalizedUrl}`)
      const texture = await existingPromise
      if (texture) {
        // ❌ NIE używaj zwykłego .clone() - kopiuje źle właściwości!
        // ✅ Ręcznie skopiuj WSZYSTKIE właściwości z oryginału
        const cloned = texture.clone()
        
        // RĘCZNIE skopiuj WSZYSTKIE właściwości jakości z oryginału
        cloned.wrapS = texture.wrapS
        cloned.wrapT = texture.wrapT
        cloned.generateMipmaps = texture.generateMipmaps
        cloned.minFilter = texture.minFilter
        cloned.magFilter = texture.magFilter
        cloned.anisotropy = texture.anisotropy
        cloned.colorSpace = texture.colorSpace
        
        if (options.repeat) {
          cloned.repeat.set(options.repeat[0], options.repeat[1])
        }
        
        // WYMUSZAJ UPDATE - to zapewnia że wszystkie ustawienia są zastosowane
        cloned.needsUpdate = true
        
        return cloned
      }
      return undefined
    }

    // 🚀 Twórz nowy Promise i zapisz w cache PRZED fetch
    DEBUG_LOGS && console.log(`🔄 Ładuję NOWĄ teksturę: ${normalizedUrl}`)
    const loadPromise = (async (): Promise<THREE.Texture | undefined> => {
      try {
        // Użyj cache przeglądarki dla fetch
        const response = await fetch(url, { cache: 'default' })
    if (!response.ok) {
      DEBUG_LOGS && console.error(`❌ loadTextureWithSettings: Nie udało się załadować ${url} - status: ${response.status} ${response.statusText}`)
          // Usuń Promise z cache w przypadku błędu
          textureLoadingPromises.delete(normalizedUrl)
      return undefined
    }

    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)
        DEBUG_LOGS && console.log(`📦 loadTextureWithSettings: Stworzono objectUrl dla ${normalizedUrl}, rozmiar blob: ${(blob.size / 1024 / 1024).toFixed(2)} MB, typ: ${blob.type}`)
    
    const texture = await loader.loadAsync(objectUrl)
    URL.revokeObjectURL(objectUrl) // Revoke PO załadowaniu tekstury!
    
    const fileSizeMB = (blob.size / 1024 / 1024).toFixed(2)
        const isLargeJPG = normalizedUrl.includes('.jpg') && parseFloat(fileSizeMB) > 30
        const isWebP = normalizedUrl.includes('.webp')
    
        console.log(`✅ loadTextureWithSettings: Załadowano teksturę ${normalizedUrl}`)
    console.log(`   📊 Rozmiar: ${fileSizeMB} MB, typ: ${blob.type}`)
    
    if (isLargeJPG) {
          console.warn(`⚠️ UWAGA: Ładuje się DUŻY JPG (${fileSizeMB} MB) - powinno być webp (~4.5 MB)! URL: ${normalizedUrl}`)
    } else if (isWebP) {
      console.log(`✅ OK: Załadowano WebP (${fileSizeMB} MB) - to jest dobre!`)
    }

        // 🚀 Konfiguracja tekstury z mipmaps i anisotropic filtering
    texture.generateMipmaps = true
    texture.minFilter = THREE.LinearMipmapLinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.RepeatWrapping
    texture.anisotropy = MAX_TEXTURE_ANISOTROPY
    texture.colorSpace = options.colorMap ? THREE.SRGBColorSpace : LINEAR_COLOR_SPACE

        // NIE ustawiaj repeat tutaj - to będzie zrobione w clone
        // texture.repeat zostanie ustawiony w clone jeśli options.repeat jest podany

        // 🚀 WYMUSZAJ aktualizację i generowanie mipmaps
    texture.needsUpdate = true

        // 🚀 POZIOM 1: Zapisz w globalnym cache z NORMALIZOWANYM URL
        globalTextureCache.set(normalizedUrl, texture)
        DEBUG_LOGS && console.log(`💾 Zapisano teksturę w globalnym cache: ${normalizedUrl}`)

        // 🚀 POZIOM 2: Usuń Promise z cache PO zakończeniu (sukces)
        textureLoadingPromises.delete(normalizedUrl)

    return texture
      } catch (error) {
        console.error(`❌ Błąd ładowania tekstury ${normalizedUrl}:`, error)
        // 🚀 POZIOM 2: Usuń Promise z cache w przypadku błędu
        textureLoadingPromises.delete(normalizedUrl)
        return undefined
      }
    })()

    // 🚀 POZIOM 2: Zapisz Promise w cache PRZED rozpoczęciem ładowania
    textureLoadingPromises.set(normalizedUrl, loadPromise)
    
    const texture = await loadPromise
    if (texture) {
      // ❌ NIE zwracaj zwykłego clone() - kopiuje źle właściwości!
      // ✅ Ręcznie skopiuj WSZYSTKIE właściwości jakości
      const cloned = texture.clone()
      
      // RĘCZNIE skopiuj WSZYSTKIE właściwości jakości z oryginału
      cloned.wrapS = texture.wrapS
      cloned.wrapT = texture.wrapT
      cloned.generateMipmaps = texture.generateMipmaps
      cloned.minFilter = texture.minFilter
      cloned.magFilter = texture.magFilter
      cloned.anisotropy = texture.anisotropy
      cloned.colorSpace = texture.colorSpace
      
      // Zastosuj opcje (repeat może być różny dla różnych użyć)
      if (options.repeat) {
        cloned.repeat.set(options.repeat[0], options.repeat[1])
      }
      
      // WYMUSZAJ UPDATE - to zapewnia że wszystkie ustawienia są zastosowane
      cloned.needsUpdate = true
      
      return cloned
    }
    return undefined
  } catch (error) {
    console.error(`❌ Błąd w loadTextureWithSettings dla ${normalizedUrl}:`, error)
    // Usuń Promise z cache w przypadku błędu
    textureLoadingPromises.delete(normalizedUrl)
    return undefined
  }
}

async function resolveTextureFromConfig(
  loader: THREE.TextureLoader,
  textureDir: string,
  fileName: string | undefined,
  options: {
    colorMap?: boolean
    repeat?: [number, number]
  } = {}
): Promise<THREE.Texture | undefined> {
  if (!fileName) return undefined

  const basePath = `${textureDir}/${fileName}`
  const candidates: string[] = []

  if (fileName.toLowerCase().endsWith('.tif')) {
    candidates.push(basePath.replace(/\.tif$/i, '.png'))
  }
  candidates.push(basePath)

  for (const candidate of candidates) {
    const texture = await loadTextureWithSettings(loader, candidate, options)
    if (texture) return texture
  }

  return undefined
}

async function populateAdditionalMaps(
  target:
    | LoadedTextures['wood']
    | LoadedTextures['metal']
    | undefined,
  loader: THREE.TextureLoader,
  textureDir: string,
  config: TextureConfig,
  baseTexture?: THREE.Texture
) {
  if (!target) return

  // 🚀 OPTYMALIZACJA: Ładuj tylko mapy, które istnieją jako pliki
  // NIE generuj map z diffuse - to jest EKSTREMALNIE wolne (przetwarza każdy piksel)
  // Three.js radzi sobie bez nich - używa domyślnych wartości materiału
  
  const singleDiffuse = Array.isArray(target.diffuse) ? target.diffuse[0] : target.diffuse
  const repeatValue: [number, number] = [singleDiffuse?.repeat?.x ?? 2, singleDiffuse?.repeat?.y ?? 2]
  
  if (!target.normal && config.normal) {
    target.normal = await resolveTextureFromConfig(loader, textureDir, config.normal, { repeat: repeatValue })
  }

  if (!target.roughness && config.roughness) {
    target.roughness = await resolveTextureFromConfig(loader, textureDir, config.roughness, { repeat: repeatValue })
  }

  if (!target.metalness && config.metalness) {
    target.metalness = await resolveTextureFromConfig(loader, textureDir, config.metalness, { repeat: repeatValue })
  }

  if (!target.ao && config.ao) {
    target.ao = await resolveTextureFromConfig(loader, textureDir, config.ao, { repeat: repeatValue })
  }

  if (!target.height && config.height) {
    target.height = await resolveTextureFromConfig(loader, textureDir, config.height, { repeat: repeatValue })
  }
}

/**
 * Cache dla załadowanych tekstur (optymalizacja)
 */
const textureCache = new Map<string, LoadedTextures>()

/**
 * Cache dla wygenerowanych map (normal, roughness, metalness, ao, height)
 * Klucz: UUID tekstury diffuse + typ mapy
 */
const generatedMapsCache = new Map<string, THREE.Texture>()

/**
 * Ładuje tekstury dla wybranego ogrodzenia i koloru z obsługą różnych grup
 */
export async function loadTexturesForFence(
  fenceData: FenceConfig,
  color?: string
): Promise<LoadedTextures | undefined> {
  try {
    // 🔄 Najpierw załaduj konfigurację, żeby uzyskać aktualną ścieżkę tekstury dla cache key
    // ⚠️ WAŻNE: Jeśli fenceData.id to już ID wariantu (np. lupane-muro-p22), to użyj go zamiast ładować ponownie
    // Dla mix configs (royal-mix, lupane-muro-mix) fenceData.id powinien być już ID wariantu po podmianie w elementManager
    // ⚠️ WAŻNE: Jeśli fenceData ma już pełny config z elements (np. po podmianie w elementManager), użyj go zamiast ładować ponownie
    let fenceConfig: FenceConfig = fenceData
    // Sprawdź, czy fenceData ma już pełny config z elements (po podmianie w elementManager)
    if (!fenceData.elements || !fenceData.elements.post || !fenceData.elements.section) {
      // Jeśli nie, załaduj config ponownie
      const loadedConfig = await getFenceConfigWithColor(fenceData.id, color)
      if (!loadedConfig) return undefined
      fenceConfig = loadedConfig
    }

    DEBUG_LOGS && console.log(`🔍 DEBUG: loadTexturesForFence - fenceData.id: ${fenceData.id}, fenceConfig.id: ${fenceConfig.id}, color: ${color}`)
    DEBUG_LOGS && console.log(`🔍 DEBUG: loadTexturesForFence - fenceConfig.elements?.post?.groups:`, fenceConfig.elements?.post?.groups)
    DEBUG_LOGS && console.log(`🔍 DEBUG: loadTexturesForFence - fenceConfig.elements?.section?.groups:`, fenceConfig.elements?.section?.groups)
    
    // 🎯 Utwórz cache key który uwzględnia ścieżkę tekstury (żeby cache się invalidował przy zmianie konfiguracji)
    const simpleCacheKey = `${fenceData.id}_${color || 'default'}`
    
    // Dodaj hash ścieżki tekstury do cache key (jeśli istnieje colorTextures)
    let texturePathHash = ''
    if (fenceConfig.colorTextures && color && fenceConfig.colorTextures[color]?.materialTexture) {
      const materialTexturePath = fenceConfig.colorTextures[color].materialTexture
      const firstPath = Array.isArray(materialTexturePath) ? materialTexturePath[0] : materialTexturePath
      // Użyj prostego hash z nazwy pliku (bez pełnej ścieżki)
      const fileName = firstPath.substring(firstPath.lastIndexOf('/') + 1)
      texturePathHash = `_${fileName.substring(0, 30)}` // Ogranicz długość
    }
    
    // Pełny cache key z hash grup + ścieżką tekstury
    // Uproszczony hash grup - tylko nazwy, nie pełna struktura
    const postGroups = fenceConfig?.elements?.post?.groups || []
    const sectionGroups = fenceConfig?.elements?.section?.groups || []
    const allGroups = [...postGroups, ...sectionGroups].sort().join(',') // Sortuj dla spójności
    const groupsHash = allGroups.length > 0 ? `_${allGroups.substring(0, 50)}` : ''
    const cacheKey = `${simpleCacheKey}${texturePathHash}${groupsHash}`
    
    DEBUG_LOGS && console.log(`🔍 loadTexturesForFence: Sprawdzam cache dla key: ${cacheKey.substring(0, 100)}...`)
    
    // 🚀 Sprawdź cache z nowym kluczem
    const cachedTextures = textureCache.get(cacheKey)
    if (cachedTextures) {
      DEBUG_LOGS && console.log(`✅ CACHE HIT (loadTexturesForFence): Użyto tekstury z cache: ${cacheKey.substring(0, 100)}...`)
      return cachedTextures
    }
    DEBUG_LOGS && console.log(`🔄 Ładuję NOWE tekstury (key: ${cacheKey.substring(0, 100)}...)`)

    const textureLoader = new THREE.TextureLoader()
    let woodMaps: LoadedTextures['wood'] | undefined
    let metalMaps: LoadedTextures['metal'] | undefined

    // 🎯 Ładuj tekstury dla wszystkich typów elementów (post, section, gate, door)
    const elementTypes = ['post', 'section', 'gate', 'door'] as const
    
    for (const elementType of elementTypes) {
      const elementTextures = fenceConfig.elements?.[elementType]?.textures
      if (!elementTextures) continue
      
      DEBUG_LOGS && console.log(`🔍 DEBUG: fenceConfig.elements?.${elementType}?.textures:`, elementTextures)
      
      // Dynamicznie ładuj tekstury dla wszystkich grup z konfiguracji
      for (const [groupName, textureConfig] of Object.entries(elementTextures)) {
        DEBUG_LOGS && console.log(`🔍 DEBUG: Przetwarzam grupę ${groupName} dla ${elementType}:`, textureConfig)
        if (textureConfig && typeof textureConfig === 'object' && 'diffuse' in textureConfig) {
          const textureType = textureConfig.type || 'wood' // domyślnie drewno
          
          // 🎯 Dla Vision Bet i innych systemów z colorTextures: użyj materialTexture z colorTextures zamiast textureConfig.diffuse
          let textureDir: string
          let diffuseFileName: string
          
          // 🎯 NAPRAWIONE: Używaj colorTextures dla KAŻDEGO ogrodzenia które je ma
          // (poprzednio działało tylko dla slim-3d, gladkie_medium, vision-bet — BUG!)
          const shouldUseColorTextures = !!(fenceConfig.colorTextures && color && fenceConfig.colorTextures[color])
          
          DEBUG_LOGS && console.log(`🔍 DEBUG: shouldUseColorTextures=${shouldUseColorTextures}, fenceConfig.id=${fenceConfig.id}, color=${color}`)
          
          // 🎯 WAŻNE: Dla typu 'metal' używaj aluminumTexture, a dla 'wood' materialTexture!
          if (textureType === 'metal' && shouldUseColorTextures && fenceConfig.colorTextures && color && fenceConfig.colorTextures[color]?.aluminumTexture) {
            // Dla metalu: użyj aluminumTexture z colorTextures
            const aluminumTexturePath = fenceConfig.colorTextures[color].aluminumTexture
            textureDir = aluminumTexturePath.substring(0, aluminumTexturePath.lastIndexOf('/'))
            diffuseFileName = aluminumTexturePath.substring(aluminumTexturePath.lastIndexOf('/') + 1)
            DEBUG_LOGS && console.log(`🔩 ${fenceConfig.id}: używam aluminumTexture z colorTextures dla metalu: ${aluminumTexturePath}`)
          } else if (textureType === 'metal') {
            // 🎯 FIX: Metal z pustym aluminumTexture — użyj diffuse z textures config (np. Aluminum.jpg)
            // zamiast wpadać do bloku materialTexture, który załadowałby kolorową teksturę betonu
            const colorConfig = shouldUseColorTextures && fenceConfig.colorTextures && color ? fenceConfig.colorTextures[color] : null
            const modelPath = colorConfig?.modelPath || fenceConfig.modelPath || fenceConfig.daeFile || ''
            textureDir = modelPath ? modelPath.substring(0, modelPath.lastIndexOf('/')) : (fenceConfig.texturePath || '/Ogrodzenie')
            diffuseFileName = textureConfig.diffuse // np. "Aluminum.jpg"
            DEBUG_LOGS && console.log(`🔩 ${fenceConfig.id}: metal z pustym aluminumTexture — fallback do ${textureDir}/${diffuseFileName}`)
          } else if (shouldUseColorTextures && fenceConfig.colorTextures && color && fenceConfig.colorTextures[color]) {
            // Dla drewna: użyj materialTexture z colorTextures (np. material_1_q60.webp)
            const materialTexturePath = fenceConfig.colorTextures[color].materialTexture
            const firstPath = Array.isArray(materialTexturePath) ? materialTexturePath[0] : materialTexturePath
            textureDir = firstPath.substring(0, firstPath.lastIndexOf('/'))
            diffuseFileName = firstPath.substring(firstPath.lastIndexOf('/') + 1)
            DEBUG_LOGS && console.log(`🎯 ${fenceConfig.id}: używam materialTexture z colorTextures:`, materialTexturePath)
            DEBUG_LOGS && console.log(`🎯 ${fenceConfig.id}: textureDir = ${textureDir}, diffuseFileName = ${diffuseFileName}`)
          } else if (shouldUseColorTextures) {
            // Fallback: użyj texturePath z konfiguracji
            DEBUG_LOGS && console.log(`⚠️ ${fenceConfig.id}: Brak colorTextures dla koloru ${color}, używam texturePath: ${fenceConfig.texturePath}`)
            textureDir = fenceConfig.texturePath ? fenceConfig.texturePath.substring(0, fenceConfig.texturePath.lastIndexOf('/')) : '/Ogrodzenie'
            diffuseFileName = textureConfig.diffuse
          } else {
            // Standardowa logika: fenceConfig.texturePath zawiera pełną ścieżkę do pliku, więc muszę wyciągnąć katalog
            textureDir = fenceConfig.texturePath ? fenceConfig.texturePath.substring(0, fenceConfig.texturePath.lastIndexOf('/')) : '/Ogrodzenie'
            diffuseFileName = textureConfig.diffuse
          }
          
          const repeatValue: [number, number] = textureType === 'metal' ? [1, 1] : [2, 2]
          DEBUG_LOGS && console.log(`🔍 DEBUG: Ładuję teksturę z katalogu: ${textureDir}, plik: ${diffuseFileName} dla grupy ${groupName} (typ: ${textureType})`)

          // Dla colorTextures używamy pełnej ścieżki, dla innych resolveTextureFromConfig
          let diffuseTexture: THREE.Texture | THREE.Texture[] | undefined
          if (textureType === 'metal' && shouldUseColorTextures && fenceConfig.colorTextures && color && fenceConfig.colorTextures[color]?.aluminumTexture) {
            // Dla metalu: użyj aluminumTexture z pełną ścieżką
            // 🚀 OPTYMALIZACJA: Sprawdź cache czy webp istnieje, żeby nie robić podwójnych żądań
            let aluminumTexturePath = fenceConfig.colorTextures[color].aluminumTexture
            // Jeśli to .jpg, sprawdź czy webp istnieje (z cache)
            if (aluminumTexturePath.endsWith('.jpg')) {
              const webpPath = aluminumTexturePath.replace(/\.jpg$/, '.webp')
              const webpExists = webpExistsCache.get(aluminumTexturePath)
              
              if (webpExists === true) {
                // WebP istnieje - użyj go
                diffuseTexture = await loadTextureWithSettings(textureLoader, webpPath, {
                  colorMap: true,
                  repeat: repeatValue
                })
                DEBUG_LOGS && console.log(`✅ OPTYMALIZACJA: Używam WebP dla metalu (z cache): ${webpPath}`)
              } else if (webpExists === false) {
                // WebP nie istnieje - użyj JPG bezpośrednio
                diffuseTexture = await loadTextureWithSettings(textureLoader, aluminumTexturePath, {
                  colorMap: true,
                  repeat: repeatValue
                })
                DEBUG_LOGS && console.log(`⚠️ WebP nie istnieje dla metalu (z cache), używam JPG: ${aluminumTexturePath}`)
              } else {
                // Nie sprawdzono jeszcze - spróbuj webp, zapisz wynik w cache
                try {
                  const webpTexture = await loadTextureWithSettings(textureLoader, webpPath, {
                    colorMap: true,
                    repeat: repeatValue
                  })
                  if (webpTexture) {
                    diffuseTexture = webpTexture
                    webpExistsCache.set(aluminumTexturePath, true)
                    DEBUG_LOGS && console.log(`✅ OPTYMALIZACJA: Używam WebP dla metalu (nowy): ${webpPath}`)
                  } else {
                    throw new Error('WebP load failed')
                  }
                } catch {
                  // WebP nie istnieje - zapisz w cache i użyj JPG
                  webpExistsCache.set(aluminumTexturePath, false)
                  diffuseTexture = await loadTextureWithSettings(textureLoader, aluminumTexturePath, {
                    colorMap: true,
                    repeat: repeatValue
                  })
                  DEBUG_LOGS && console.log(`⚠️ WebP nie istnieje dla metalu (sprawdzono), używam JPG: ${aluminumTexturePath}`)
                }
              }
            } else {
              // Nie jest .jpg, użyj bezpośrednio
              diffuseTexture = await loadTextureWithSettings(textureLoader, aluminumTexturePath, {
                colorMap: true,
                repeat: repeatValue
              })
            }
          } else if (textureType === 'metal') {
            // 🎯 FIX: Metal z pustym aluminumTexture — załaduj z textureDir/diffuseFileName (ustawione wyżej)
            const metalTexturePath = `${textureDir}/${diffuseFileName}`
            DEBUG_LOGS && console.log(`🔩 ${fenceConfig.id}: ładuję metal fallback z: ${metalTexturePath}`)
            diffuseTexture = await resolveTextureFromConfig(textureLoader, textureDir, diffuseFileName, {
              colorMap: true,
              repeat: repeatValue
            })
          } else if (shouldUseColorTextures && fenceConfig.colorTextures && color && fenceConfig.colorTextures[color]?.materialTexture) {
            // Dla drewna: użyj materialTexture z pełną ścieżką
            let materialTexturePath = fenceConfig.colorTextures[color].materialTexture
            
            if (Array.isArray(materialTexturePath)) {
              diffuseTexture = await Promise.all(materialTexturePath.map(async path => {
                return await loadTextureWithSettings(textureLoader, path, {
                  colorMap: true,
                  repeat: repeatValue
                })
              })) as THREE.Texture[]
              // Oznacz tekstury z Konfiguratora
              for (const t of diffuseTexture) {
                (t as any)._fromKonfigurator = true
              }
              DEBUG_LOGS && console.log(`✅ Załadowano tablicę ${diffuseTexture.length} tekstur z Konfiguratora`)
            } else {
              // 🚀 OPTYMALIZACJA: Sprawdź cache czy webp istnieje, żeby nie robić podwójnych żądań
              // Jeśli to .jpg, sprawdź czy webp istnieje (z cache)
              if (materialTexturePath.endsWith('.jpg')) {
              const webpPath = materialTexturePath.replace(/\.jpg$/, '.webp')
              const webpExists = webpExistsCache.get(materialTexturePath)
              
              if (webpExists === true) {
                // WebP istnieje - użyj go
                diffuseTexture = await loadTextureWithSettings(textureLoader, webpPath, {
                  colorMap: true,
                  repeat: repeatValue
                })
                DEBUG_LOGS && console.log(`✅ OPTYMALIZACJA: Używam WebP (z cache): ${webpPath}`)
              } else if (webpExists === false) {
                // WebP nie istnieje - użyj JPG bezpośrednio
                diffuseTexture = await loadTextureWithSettings(textureLoader, materialTexturePath, {
                  colorMap: true,
                  repeat: repeatValue
                })
                DEBUG_LOGS && console.log(`⚠️ WebP nie istnieje (z cache), używam JPG: ${materialTexturePath}`)
              } else {
                // Nie sprawdzono jeszcze - spróbuj webp, zapisz wynik w cache
                try {
                  const webpTexture = await loadTextureWithSettings(textureLoader, webpPath, {
                    colorMap: true,
                    repeat: repeatValue
                  })
                  if (webpTexture) {
                    diffuseTexture = webpTexture
                    webpExistsCache.set(materialTexturePath, true)
                    DEBUG_LOGS && console.log(`✅ OPTYMALIZACJA: Używam WebP (nowy): ${webpPath}`)
                  } else {
                    throw new Error('WebP load failed')
                  }
                } catch {
                  // WebP nie istnieje - zapisz w cache i użyj JPG
                  webpExistsCache.set(materialTexturePath, false)
                  diffuseTexture = await loadTextureWithSettings(textureLoader, materialTexturePath, {
                    colorMap: true,
                    repeat: repeatValue
                  })
                  DEBUG_LOGS && console.log(`⚠️ WebP nie istnieje (sprawdzono), używam JPG: ${materialTexturePath}`)
                }
              }
            } else {
              // Nie jest .jpg, użyj bezpośrednio
              diffuseTexture = await loadTextureWithSettings(textureLoader, materialTexturePath, {
                colorMap: true,
                repeat: repeatValue
              })
            }
            } // Koniec bloku if(Array.isArray(materialTexturePath))
          } else {
            // Standardowa logika z resolveTextureFromConfig
            diffuseTexture = await resolveTextureFromConfig(textureLoader, textureDir, diffuseFileName, {
              colorMap: true,
              repeat: repeatValue
            })
          }

          if (diffuseTexture) {
            if (textureType === 'metal') {
              // 🚀 OPTYMALIZACJA: Użyj tej samej tekstury dla wszystkich grup metalu (nie duplikuj)
              if (!metalMaps) {
                metalMaps = { diffuse: diffuseTexture }
                DEBUG_LOGS && console.log(`✅ Załadowano teksturę metalu dla ${groupName} (${elementType})`)
                const baseTexture = Array.isArray(diffuseTexture) ? diffuseTexture[0] : diffuseTexture
                await populateAdditionalMaps(metalMaps, textureLoader, textureDir, textureConfig, baseTexture)
              } else {
                // Tekstura metalu już załadowana - użyj tej samej (cache już zadziałał, ale upewnijmy się)
                DEBUG_LOGS && console.log(`♻️ Używam już załadowanej tekstury metalu dla ${groupName} (${elementType})`)
              }
            } else {
              // 🚀 OPTYMALIZACJA: Użyj tej samej tekstury dla wszystkich grup drewna (nie duplikuj)
              if (!woodMaps) {
                woodMaps = { diffuse: diffuseTexture }
                DEBUG_LOGS && console.log(`✅ Załadowano teksturę drewna dla ${groupName} (${elementType})`)
                const baseTexture = Array.isArray(diffuseTexture) ? diffuseTexture[0] : diffuseTexture
                await populateAdditionalMaps(woodMaps, textureLoader, textureDir, textureConfig, baseTexture)
              } else {
                // Tekstura drewna już załadowana - użyj tej samej (cache już zadziałał, ale upewnijmy się)
                DEBUG_LOGS && console.log(`♻️ Używam już załadowanej tekstury drewna dla ${groupName} (${elementType})`)
              }
            }
          } else {
            console.error(`❌ Nie udało się załadować mapy diffuse dla ${groupName} (${elementType})`)
          }
        }
      }
    }
    
    // Jeśli nie znaleziono tekstur w konfiguracji grup, użyj starej logiki
    if (!woodMaps?.diffuse && !metalMaps?.diffuse) {
      // Stara logika dla kompatybilności wstecznej
      let materialUrl = ''
      let aluminumUrl = ''

      DEBUG_LOGS && console.log(`🔍 DEBUG: Używam starej logiki - fenceConfig.id=${fenceConfig.id}, color=${color}`)
      DEBUG_LOGS && console.log(`🔍 DEBUG: fenceConfig.colorTextures:`, fenceConfig.colorTextures ? Object.keys(fenceConfig.colorTextures) : 'brak')
      
      if (fenceConfig.colorTextures && color && fenceConfig.colorTextures[color]) {
        const colorConfig = fenceConfig.colorTextures[color]
        materialUrl = colorConfig.materialTexture
        aluminumUrl = colorConfig.aluminumTexture
        DEBUG_LOGS && console.log(`🎯 DEBUG: colorConfig.materialTexture = ${materialUrl}`)
        DEBUG_LOGS && console.log(`🎯 DEBUG: colorConfig.aluminumTexture = ${aluminumUrl}`)
      } else {
        // Fallback do podstawowych tekstur
        materialUrl = `/Ogrodzenie/material_1.png`
        aluminumUrl = `/Ogrodzenie/Aluminum.jpg`
        DEBUG_LOGS && console.log(`⚠️ DEBUG: Brak colorTextures, używam fallback: ${materialUrl}`)
      }

      if (!materialUrl) {
        DEBUG_LOGS && console.error(`❌ DEBUG: materialUrl jest puste!`)
        return undefined
      }

      DEBUG_LOGS && console.log(`🔄 DEBUG: Ładuję teksturę: ${materialUrl}`)
      const fallbackWood = await loadTextureWithSettings(textureLoader, materialUrl, {
        colorMap: true,
        repeat: [2, 2]
      })
      
      DEBUG_LOGS && console.log(`✅ DEBUG: fallbackWood załadowana:`, fallbackWood ? 'TAK' : 'NIE')

      if (fallbackWood) {
        woodMaps = woodMaps ?? {}
        woodMaps.diffuse = fallbackWood
        await populateAdditionalMaps(woodMaps, textureLoader, '/Ogrodzenie', { diffuse: 'material_1.png' }, fallbackWood)
      }

      if (aluminumUrl) {
        const fallbackMetal = await loadTextureWithSettings(textureLoader, aluminumUrl, {
          colorMap: true,
          repeat: [1, 1]
        })
        if (fallbackMetal) {
          metalMaps = metalMaps ?? {}
          metalMaps.diffuse = fallbackMetal
          // Użyj właściwego katalogu zamiast hardcoded '/Ogrodzenie'
          const aluminumDir = aluminumUrl.substring(0, aluminumUrl.lastIndexOf('/'))
          await populateAdditionalMaps(metalMaps, textureLoader, aluminumDir, { diffuse: 'Aluminum.jpg' }, fallbackMetal)
          DEBUG_LOGS && console.log(`✅ Załadowano teksturę aluminium z konfiguracji: ${aluminumUrl}`)
        } else {
          console.warn(`⚠️ Nie udało się załadować tekstury aluminium: ${aluminumUrl}`)
        }
      } else {
        console.warn(`⚠️ Brak aluminumUrl w konfiguracji dla ${fenceConfig.id}, kolor: ${color}`)
      }
    }

    // ❌ USUNIĘTE: Fallback który używał drewna zamiast metalu - to powodowało że metal miał główną teksturę!
    // Teraz jeśli nie ma tekstury metalu, po prostu nie będzie tekstury metalu (będzie używać materiału domyślnego)

    if (woodMaps?.diffuse || metalMaps?.diffuse) {
      const result: LoadedTextures = {}
      if (woodMaps) result.wood = woodMaps
      if (metalMaps) result.metal = metalMaps
      
      // 🎯 CACHE: Zapisz w cache
      textureCache.set(cacheKey, result)
      DEBUG_LOGS && console.log(`💾 Zapisano tekstury w cache: ${cacheKey}`)
      
      return result
    }

    return undefined
  } catch (error) {
    console.error('Błąd ładowania tekstur:', error)
    return undefined
  }
}

/**
 * Znajduje i ładuje mapy tekstur (normal, roughness, metalness, AO, height)
 */
export async function findTextureMaps(
  texturePath: string,
  baseName: string
): Promise<Partial<Record<'diffuse' | 'normal' | 'roughness' | 'metalness' | 'ao' | 'height', THREE.Texture>>> {
  const cacheKey = `${texturePath}_${baseName}`
  
  // Sprawdź cache
  const cached = textureCache.get(cacheKey)
  if (cached?.wood) return cached.wood

  const maps: any = {}
  const textureLoader = new THREE.TextureLoader()

  const loadPromises: Promise<{ type: string, texture: THREE.Texture | null, name: string }>[] = []

  // Diffuse maps
  const diffusePriority = ['material_1', 'material_1_0', 'diffuse', 'albedo', 'base_color']
  diffusePriority.forEach(name => {
    loadPromises.push(
      textureLoader.loadAsync(`${texturePath}/${name}.jpg`)
        .then(texture => ({ type: 'diffuse', texture, name: `${name}.jpg` }))
        .catch(() => 
          textureLoader.loadAsync(`${texturePath}/${name}.png`)
            .then(texture => ({ type: 'diffuse', texture, name: `${name}.png` }))
            .catch(() => ({ type: 'diffuse', texture: null, name: `${name}` }))
        )
    )
  })

  const results = await Promise.allSettled(loadPromises)

  results.forEach((result) => {
    if (result.status === 'fulfilled' && result.value.texture) {
      const { type, texture } = result.value
      if (!maps[type]) {
        maps[type] = texture
        // Konfiguracja tekstury
        texture.wrapS = THREE.RepeatWrapping
        texture.wrapT = THREE.RepeatWrapping
        texture.repeat.set(2, 2)
        texture.generateMipmaps = true
        texture.anisotropy = 16
      }
    }
  })

  // 🚀 OPTYMALIZACJA: NIE generujemy map automatycznie - to jest EKSTREMALNIE wolne
  // Three.js radzi sobie bez nich - używa domyślnych wartości materiału
  // Jeśli potrzebujesz tych map, dodaj je jako pliki w folderze tekstur

  // Zapisz w cache
  const cachedMaps = { wood: maps, metal: maps }
  textureCache.set(cacheKey, cachedMaps)

  return maps
}

/**
 * Generuje normal map z diffuse texture (z cache)
 */
async function generateNormalMap(diffuseTexture: THREE.Texture): Promise<THREE.Texture> {
  // 🚀 OPTYMALIZACJA: Sprawdź cache przed generowaniem
  const cacheKey = `normal_${diffuseTexture.uuid}`
  const cached = generatedMapsCache.get(cacheKey)
  if (cached) return cached.clone()

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.width = diffuseTexture.image.width
  canvas.height = diffuseTexture.image.height

  ctx.drawImage(diffuseTexture.image, 0, 0)
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  for (let i = 0; i < data.length; i += 4) {
    const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3 / 255
    data[i] = 128
    data[i + 1] = 128
    data[i + 2] = Math.floor(brightness * 255)
    data[i + 3] = 255
  }

  ctx.putImageData(imageData, 0, 0)
  const normalTexture = new THREE.CanvasTexture(canvas)
  normalTexture.wrapS = THREE.RepeatWrapping
  normalTexture.wrapT = THREE.RepeatWrapping
  normalTexture.repeat.set(2, 2)
  normalTexture.generateMipmaps = true
  normalTexture.anisotropy = 16

  // Zapisz w cache
  generatedMapsCache.set(cacheKey, normalTexture)
  return normalTexture
}

/**
 * Generuje roughness map z diffuse texture (z cache)
 */
async function generateRoughnessMap(diffuseTexture: THREE.Texture): Promise<THREE.Texture> {
  // 🚀 OPTYMALIZACJA: Sprawdź cache przed generowaniem
  const cacheKey = `roughness_${diffuseTexture.uuid}`
  const cached = generatedMapsCache.get(cacheKey)
  if (cached) return cached.clone()

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.width = diffuseTexture.image.width
  canvas.height = diffuseTexture.image.height

  ctx.drawImage(diffuseTexture.image, 0, 0)
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  for (let i = 0; i < data.length; i += 4) {
    const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3 / 255
    const roughness = Math.max(0.1, Math.min(0.9, 0.3 + brightness * 0.4))
    const value = Math.floor(roughness * 255)
    data[i] = value
    data[i + 1] = value
    data[i + 2] = value
    data[i + 3] = 255
  }

  ctx.putImageData(imageData, 0, 0)
  const roughnessTexture = new THREE.CanvasTexture(canvas)
  roughnessTexture.wrapS = THREE.RepeatWrapping
  roughnessTexture.wrapT = THREE.RepeatWrapping
  roughnessTexture.repeat.set(2, 2)
  roughnessTexture.generateMipmaps = true
  roughnessTexture.anisotropy = 16

  // Zapisz w cache
  generatedMapsCache.set(cacheKey, roughnessTexture)
  return roughnessTexture
}

/**
 * Generuje metalness map z diffuse texture (z cache)
 */
async function generateMetalnessMap(diffuseTexture: THREE.Texture): Promise<THREE.Texture> {
  // 🚀 OPTYMALIZACJA: Sprawdź cache przed generowaniem
  const cacheKey = `metalness_${diffuseTexture.uuid}`
  const cached = generatedMapsCache.get(cacheKey)
  if (cached) return cached.clone()

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.width = diffuseTexture.image.width
  canvas.height = diffuseTexture.image.height

  ctx.drawImage(diffuseTexture.image, 0, 0)
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i] / 255
    const g = data[i + 1] / 255
    const b = data[i + 2] / 255
    const brightness = (r + g + b) / 3

    const metalness = Math.max(0.0, Math.min(1.0, 0.1 + (1 - brightness) * 0.8))
    const value = Math.floor(metalness * 255)
    data[i] = value
    data[i + 1] = value
    data[i + 2] = value
    data[i + 3] = 255
  }

  ctx.putImageData(imageData, 0, 0)
  const metalnessTexture = new THREE.CanvasTexture(canvas)
  metalnessTexture.wrapS = THREE.RepeatWrapping
  metalnessTexture.wrapT = THREE.RepeatWrapping
  metalnessTexture.repeat.set(2, 2)
  metalnessTexture.generateMipmaps = true
  metalnessTexture.anisotropy = 16

  // Zapisz w cache
  generatedMapsCache.set(cacheKey, metalnessTexture)
  return metalnessTexture
}

/**
 * Generuje AO (Ambient Occlusion) map z diffuse texture (z cache)
 */
async function generateAOMap(diffuseTexture: THREE.Texture): Promise<THREE.Texture> {
  // 🚀 OPTYMALIZACJA: Sprawdź cache przed generowaniem
  const cacheKey = `ao_${diffuseTexture.uuid}`
  const cached = generatedMapsCache.get(cacheKey)
  if (cached) return cached.clone()

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.width = diffuseTexture.image.width
  canvas.height = diffuseTexture.image.height

  ctx.drawImage(diffuseTexture.image, 0, 0)
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  for (let i = 0; i < data.length; i += 4) {
    const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3 / 255
    const ao = Math.max(0.2, Math.min(1.0, 0.5 + brightness * 0.3))
    const value = Math.floor(ao * 255)
    data[i] = value
    data[i + 1] = value
    data[i + 2] = value
    data[i + 3] = 255
  }

  ctx.putImageData(imageData, 0, 0)
  const aoTexture = new THREE.CanvasTexture(canvas)
  aoTexture.wrapS = THREE.RepeatWrapping
  aoTexture.wrapT = THREE.RepeatWrapping
  aoTexture.repeat.set(2, 2)
  aoTexture.generateMipmaps = true
  aoTexture.anisotropy = 16

  // Zapisz w cache
  generatedMapsCache.set(cacheKey, aoTexture)
  return aoTexture
}

/**
 * Generuje height (displacement) map z diffuse texture (z cache)
 */
async function generateHeightMap(diffuseTexture: THREE.Texture): Promise<THREE.Texture> {
  // 🚀 OPTYMALIZACJA: Sprawdź cache przed generowaniem
  const cacheKey = `height_${diffuseTexture.uuid}`
  const cached = generatedMapsCache.get(cacheKey)
  if (cached) return cached.clone()

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  canvas.width = diffuseTexture.image.width
  canvas.height = diffuseTexture.image.height

  ctx.drawImage(diffuseTexture.image, 0, 0)
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const data = imageData.data

  for (let i = 0; i < data.length; i += 4) {
    const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3 / 255
    const height = Math.max(0.0, Math.min(1.0, brightness * 0.5))
    const value = Math.floor(height * 255)
    data[i] = value
    data[i + 1] = value
    data[i + 2] = value
    data[i + 3] = 255
  }

  ctx.putImageData(imageData, 0, 0)
  const heightTexture = new THREE.CanvasTexture(canvas)
  heightTexture.wrapS = THREE.RepeatWrapping
  heightTexture.wrapT = THREE.RepeatWrapping
  heightTexture.repeat.set(2, 2)
  heightTexture.generateMipmaps = true
  heightTexture.anisotropy = 16

  // Zapisz w cache
  generatedMapsCache.set(cacheKey, heightTexture)
  return heightTexture
}

/**
 * Aplikuje tekstury do modelu 3D z obsługą różnych grup
 */
/**
 * Klonuje teksturę z podanym offsetem i repeat
 * Offset powinien być spójny w obrębie jednego elementu (post/section)
 */
function cloneTextureWithOffset(texture: THREE.Texture, repeatX: number, repeatY: number, offsetX: number, offsetY: number): THREE.Texture {
  const cloned = texture.clone()
  
  cloned.wrapS = THREE.RepeatWrapping
  cloned.wrapT = THREE.RepeatWrapping
  cloned.generateMipmaps = true
  cloned.minFilter = THREE.LinearMipmapLinearFilter
  cloned.magFilter = THREE.LinearFilter
  cloned.anisotropy = MAX_TEXTURE_ANISOTROPY
  cloned.colorSpace = texture.colorSpace || THREE.SRGBColorSpace
  
  // Normalne mapowanie tekstury (bez flipu — ujemny repeat powodował białe paski na krawędziach)
  cloned.repeat.set(repeatX, repeatY)
  cloned.offset.set(offsetX, offsetY)
  
  cloned.needsUpdate = true
  
  return cloned
}

/**
 * 🚀 POZIOM 3: Shared materials cache
 * Tworzy lub zwraca istniejący materiał dla identycznych grup
 */
function buildMaterial(
  maps: LoadedTextures['wood'] | LoadedTextures['metal'] | undefined,
  defaults: {
    roughness: number
    metalness: number
    envMapIntensity: number
  },
  repeatX: number = 1.0,
  repeatY: number = 1.0,
  offsetX: number = 0,
  offsetY: number = 0
): THREE.Material | null {
  if (!maps?.diffuse) return null

  let diffuseTexture = maps.diffuse
  if (Array.isArray(maps.diffuse)) {
    const randomIndex = Math.floor(Math.random() * maps.diffuse.length)
    diffuseTexture = maps.diffuse[randomIndex]
    console.log(`🎲 buildMaterial: Wybrano losowo teksturę ${randomIndex} z ${maps.diffuse.length}`)
  }

  const tex = diffuseTexture as THREE.Texture
  const textureMap = cloneTextureWithOffset(tex, repeatX, repeatY, offsetX, offsetY)

  // 🎯 Metal → MeshStandardMaterial (potrzebuje PBR + envMap dla odbić aluminium)
  // 🎯 Beton  → MeshLambertMaterial (jasny, bez ściemniania przez PBR)
  if (defaults.metalness > 0.5) {
    const material = new THREE.MeshStandardMaterial({
      map: textureMap,
      roughness: defaults.roughness,
      metalness: defaults.metalness,
      envMapIntensity: defaults.envMapIntensity,
      emissive: new THREE.Color(0x111111),
      emissiveIntensity: 0.1,
      side: THREE.FrontSide,
      polygonOffset: true,
      polygonOffsetFactor: 4,
      polygonOffsetUnits: 4
    })

    // Dodatkowe mapy PBR (tylko dla pojedynczych tekstur)
    if (!Array.isArray(maps.diffuse)) {
      if (maps.normal) {
        material.normalMap = cloneTextureWithOffset(maps.normal, repeatX, repeatY, offsetX, offsetY)
        material.normalScale = new THREE.Vector2(1.0, 1.0)
      }
      if (maps.roughness) {
        material.roughnessMap = cloneTextureWithOffset(maps.roughness, repeatX, repeatY, offsetX, offsetY)
      }
      if (maps.metalness) {
        material.metalnessMap = cloneTextureWithOffset(maps.metalness, repeatX, repeatY, offsetX, offsetY)
      }
      if (maps.ao) {
        material.aoMap = cloneTextureWithOffset(maps.ao, repeatX, repeatY, offsetX, offsetY)
      }
    }

    material.needsUpdate = true
    return material
  } else {
    // MeshBasicMaterial — tekstura 1:1 jak w pliku PNG, zero cieniowania
    const material = new THREE.MeshBasicMaterial({
      map: textureMap,
      side: THREE.FrontSide,
      polygonOffset: true,
      polygonOffsetFactor: 2,
      polygonOffsetUnits: 2
    })

    material.needsUpdate = true
    return material
  }
}

export function applyTexturesToModel(
  model: THREE.Object3D,
  textures: LoadedTextures | undefined,
  fenceConfig?: any
): void {
  if (!textures) return

  // 🎯 Offset tekstury — ZERO offset dla idealnego wyśrodkowania
  // Każdy bloczek musi mieć identycznie wyśrodkowaną teksturę
  // (poprzednio Math.random() * 0.12 powodował widoczne przesunięcia)
  const elementOffsetX = 0
  const elementOffsetY = 0

  const meshes: THREE.Mesh[] = []
  model.traverse((child: any) => {
    if (child.isMesh) {
      meshes.push(child)
    }
  })

  // Przetwarzaj wszystkie meshe
  for (const child of meshes) {
    if (!child.material) continue
    
    const objectName = child.name.toLowerCase()

    let materialApplied = false
    const elementTypes = ['post', 'section', 'gate', 'door'] as const
    
    const parentGroupName = child.parent?.name
    
    const woodRepeatX = 1.0
    const woodRepeatY = 1.0
    const metalRepeatX = 0.8
    const metalRepeatY = 0.8
    
    for (const elementType of elementTypes) {
      const elementTextures = fenceConfig?.elements?.[elementType]?.textures
      if (!elementTextures) continue
      
      // Dynamicznie sprawdź wszystkie grupy z konfiguracji
      for (const [groupName, textureConfig] of Object.entries(elementTextures)) {
        // Sprawdź czy mesh należy do grupy (przez nazwę obiektu LUB parent)
        const isInGroup = objectName.includes(groupName.toLowerCase()) || parentGroupName === groupName || parentGroupName?.toLowerCase() === groupName.toLowerCase()
        if (isInGroup && textureConfig && typeof textureConfig === 'object') {
          const textureType = (textureConfig as any).type || 'wood'
          
          if (textureType === 'metal') {
            console.log(`🔩 METAL: mesh="${child.name}" parent="${parentGroupName}" textures.metal=${!!textures.metal} diffuse=${!!textures.metal?.diffuse}`)
            const material = buildMaterial(textures.metal, {
              roughness: 0.3,
              metalness: 0.9,
              envMapIntensity: 2.0
            }, metalRepeatX, metalRepeatY, elementOffsetX, elementOffsetY)
            if (material) {
              child.material = material
              materialApplied = true
              console.log(`✅ METAL APPLIED: mesh="${child.name}" material=${material.type}`)
              break
            } else {
              console.log(`❌ METAL FAILED: buildMaterial returned null for mesh="${child.name}"`)
            }
          } else {
            const material = buildMaterial(textures.wood, {
              roughness: 0.8,
              metalness: 0.1,
              envMapIntensity: 1.8 // Przywrócono z 0.2
            }, woodRepeatX, woodRepeatY, elementOffsetX, elementOffsetY)
            if (material) {
              child.material = material
              materialApplied = true
              break
            }
          }
        }
      }
      
      // Jeśli znaleziono pasującą teksturę, przerwij przeszukiwanie
      if (materialApplied) break
    }
    
    // Fallback dla grup bez konfiguracji
    if (!materialApplied) {
      const material = buildMaterial(textures.wood, {
        roughness: 0.6,
        metalness: 0.2,
        envMapIntensity: 0.2
      }, woodRepeatX, woodRepeatY, elementOffsetX, elementOffsetY)
      if (material) child.material = material
    }
    
    // Stara logika dla kompatybilności wstecznej (tylko jeśli nie zastosowano materiału)
    if (!materialApplied) {
      // Stara logika dla kompatybilności wstecznej
      // Podstawowe bloki (group_0, base, post, fence)
      if (objectName.includes('group_0') || objectName.includes('base') || 
          objectName.includes('post') || objectName.includes('fence')) {
        const material = buildMaterial(textures.wood, {
          roughness: 0.8,
          metalness: 0.1,
          envMapIntensity: 0.2
        }, woodRepeatX, woodRepeatY, elementOffsetX, elementOffsetY)
        if (material) child.material = material
      }
      // Elementy dekoracyjne - używaj tekstur drewna jak bloczki podstawowe
      else if (objectName.includes('group_1') || objectName.includes('decoration') || 
               objectName.includes('top')) {
        const material = buildMaterial(textures.wood, {
          roughness: 0.8,
          metalness: 0.1,
          envMapIntensity: 0.2
        }, woodRepeatX, woodRepeatY, elementOffsetX, elementOffsetY)
        if (material) child.material = material
      }
      // Elementy metalowe - tylko jeśli nie ma konfiguracji (fallback dla starych modeli)
      // UWAGA: Nie używaj hardcoded 'group_13' - użyj tylko metalGroup z konfiguracji!
      else if (objectName.includes('metal') || objectName.includes('gate') || 
               objectName.includes('hinge')) {
        const material = buildMaterial(textures.metal, {
          roughness: 0.3,
          metalness: 0.9,
          envMapIntensity: 0.4
        }, metalRepeatX, metalRepeatY, elementOffsetX, elementOffsetY)
        if (material) child.material = material
      }
      // Domyślny materiał
      else {
        const material = buildMaterial(textures.wood, {
          roughness: 0.6,
          metalness: 0.2,
          envMapIntensity: 0.2
        }, woodRepeatX, woodRepeatY, elementOffsetX, elementOffsetY)
        if (material) child.material = material
      }
    }
  }

  // 🎯 GLOBALNY PASS: Planarny UV remap dla WSZYSTKICH meshy z teksturami Konfiguratora
  // Mapuje UV z pozycji wierzchołków — panoramiczna tekstura pasuje do bloczka
  // identycznie z obu stron, bez żadnych modyfikacji pliku tekstury
  const konfDiffuse = Array.isArray(textures.wood?.diffuse)
    ? textures.wood.diffuse[0] : textures.wood?.diffuse
  const isKonfTex = konfDiffuse && (konfDiffuse as any)._fromKonfigurator
  
  if (isKonfTex) {
    model.traverse((child: any) => {
      if (!child.isMesh || !child.geometry?.attributes?.position) return
      
      const geom = child.geometry
      const pos = geom.attributes.position
      const norm = geom.attributes.normal
      geom.computeBoundingBox()
      const box = geom.boundingBox!
      const size = new THREE.Vector3()
      box.getSize(size)
      
      const uvs = new Float32Array(pos.count * 2)
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i)
        const y = pos.getY(i)
        const z = pos.getZ(i)
        
        // Domyślnie XZ (przód/tył) — odbite w poziomie
        let u = size.x > 0.01 ? 1 - (x - box.min.x) / size.x : 0
        let v = size.z > 0.01 ? (z - box.min.z) / size.z : 0
        
        if (norm) {
          const nx = Math.abs(norm.getX(i))
          const ny = Math.abs(norm.getY(i))
          const nz = Math.abs(norm.getZ(i))
          
          if (nz > nx && nz > ny) {
            // Góra/dół — normala Z dominuje → projekcja XY
            u = size.x > 0.01 ? (x - box.min.x) / size.x : 0
            v = size.y > 0.01 ? (y - box.min.y) / size.y : 0
          } else if (nx > ny) {
            // Boki — normala X dominuje → projekcja YZ
            u = size.y > 0.01 ? (y - box.min.y) / size.y : 0
            v = size.z > 0.01 ? (z - box.min.z) / size.z : 0
          }
        }
        
        uvs[i * 2] = u
        uvs[i * 2 + 1] = v
      }
      geom.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
    })
  }
}

/**
 * Czyści cache tekstur
 */
export function clearTextureCache(): void {
  textureCache.clear()
  globalTextureCache.clear()
  textureLoadingPromises.clear()
  sharedMaterialsCache.clear()
  generatedMapsCache.clear()
  console.log('🧹 Wyczyszczono wszystkie cache tekstur')
}

/**
 * Czyści cache tekstur dla konkretnego ogrodzenia i koloru
 */
export function clearTextureCacheForFence(fenceId: string, color?: string): void {
  const simpleCacheKey = `${fenceId}_${color || 'default'}`
  const keysToDelete: string[] = []
  
  // Wyczyść textureCache (LoadedTextures)
  for (const key of textureCache.keys()) {
    if (key.startsWith(simpleCacheKey)) {
      keysToDelete.push(key)
    }
  }
  
  for (const key of keysToDelete) {
    textureCache.delete(key)
  }
  
  // Wyczyść sharedMaterialsCache
  const materialKeysToDelete: string[] = []
  for (const key of sharedMaterialsCache.keys()) {
    if (key.startsWith(simpleCacheKey)) {
      materialKeysToDelete.push(key)
    }
  }
  
  for (const key of materialKeysToDelete) {
    sharedMaterialsCache.delete(key)
  }
  
  if (keysToDelete.length > 0 || materialKeysToDelete.length > 0) {
    console.log(`🧹 Wyczyszczono cache tekstur dla ${fenceId} (kolor: ${color || 'default'}): ${keysToDelete.length} wpisów tekstur, ${materialKeysToDelete.length} materiałów`)
  }
}

/**
 * Zwraca statystyki cache (dla debugowania)
 */
export function getTextureCacheStats(): {
  globalTextures: number
  loadingPromises: number
  sharedMaterials: number
  loadedTextures: number
  generatedMaps: number
} {
  return {
    globalTextures: globalTextureCache.size,
    loadingPromises: textureLoadingPromises.size,
    sharedMaterials: sharedMaterialsCache.size,
    loadedTextures: textureCache.size,
    generatedMaps: generatedMapsCache.size
  }
}



